import React, { useState, useEffect, useCallback } from "react";
import ServiceForm from "../components/services/ServiceForm";
import EquipmentForm from "../components/services/EquipmentForm";
import ServiceTypeForm from "../components/services/ServiceTypeForm";

import colorTheme from "../components/common/color";
import getIcon from "../utils/Icons";

interface ServiceType {
  id: number;
  name: string;
  category: "consultation" | "machine" | "treatment";
  description?: string;
  duration: number;
  requires_equipment: boolean;
  is_active: boolean;
}

interface Equipment {
  id: number;
  name: string;
  type: "laser" | "EMS" | "Therapy" | "other";
  model?: string;
  status: "available" | "maintenance" | "broken" | "retired";
  owner: { id: number; first_name: string; last_name: string };
  operator?: { id: number; first_name: string; last_name: string };
}

interface Provider {
  id: number;
  first_name: string;
  last_name: string;
  role: "doctor" | "technician";
  email: string;
}

interface MedicalService {
  id: number;
  name: string;
  description?: string;
  base_fee: number;
  clinic_percentage: number;
  provider_percentage: number;
  owner_percentage: number;
  referrer_percentage: number;
  charity_percentage: number;
  is_active: boolean;
  service_type: ServiceType;
  provider: Provider;
  equipment?: Equipment;
}

interface PaginationInfo {
  data: MedicalService[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
  from: number | null;
  to: number | null;
}

interface ServicesResponse {
  categories: {
    consultation: PaginationInfo;
    machine: PaginationInfo;
    treatment: PaginationInfo;
  };
  search: string;
  per_page: number;
  totals: {
    consultation: number;
    machine: number;
    treatment: number;
    grand_total: number;
  };
}

interface GroupedService {
  equipment: Equipment | null;
  serviceType: ServiceType;
  services: MedicalService[];
  baseInfo: {
    name: string;
    base_fee: number;
    clinic_percentage: number;
    provider_percentage: number;
    owner_percentage: number;
    referrer_percentage: number;
    charity_percentage: number;
  };
}

const MedicalServicesPage: React.FC = () => {
  // Data states
  const [servicesData, setServicesData] = useState<ServicesResponse>({
    categories: {
      consultation: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: null,
        to: null,
      },
      machine: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: null,
        to: null,
      },
      treatment: {
        data: [],
        current_page: 1,
        last_page: 1,
        per_page: 15,
        total: 0,
        from: null,
        to: null,
      },
    },
    search: "",
    per_page: 15,
    totals: { consultation: 0, machine: 0, treatment: 0, grand_total: 0 },
  });

  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);

  // Search and pagination states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchDebounceTimeout, setSearchDebounceTimeout] =
    useState<NodeJS.Timeout | null>(null);
  const [categoryPages, setCategoryPages] = useState({
    consultation: 1,
    machine: 1,
    treatment: 1,
  });

  // Modal states
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showEquipmentForm, setShowEquipmentForm] = useState(false);
  const [showServiceTypeForm, setShowServiceTypeForm] = useState(false);
  const [editingService, setEditingService] = useState<MedicalService | null>(
    null
  );
  const [editingServiceType, setEditingServiceType] =
    useState<ServiceType | null>(null);
  const [activeTab, setActiveTab] = useState<
    "services" | "equipment" | "service-types"
  >("services");

  const API_BASE =
    process.env.REACT_APP_API_URL ||
    "https://dr-bassam-elayoubi.caduceuslb.com/api";

  // Debounced search function
  const debouncedSearch = useCallback(
    (query: string) => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }

      const timeout = setTimeout(() => {
        fetchServicesData(query, categoryPages);
      }, 300);

      setSearchDebounceTimeout(timeout);
    },
    [categoryPages, searchDebounceTimeout]
  );

  // Handle search input change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Handle page change for a specific category
  const handlePageChange = (
    category: keyof typeof categoryPages,
    page: number
  ) => {
    const newPages = { ...categoryPages, [category]: page };
    setCategoryPages(newPages);
    fetchServicesData(searchQuery, newPages);
  };

  // Fetch services data with search and pagination
  const fetchServicesData = async (search = "", pages = categoryPages) => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const queryParams = new URLSearchParams({
        search,
        per_page: "15",
        consultation_page: pages.consultation.toString(),
        machine_page: pages.machine.toString(),
        treatment_page: pages.treatment.toString(),
      });

      const response = await fetch(
        `${API_BASE}/medical-services?${queryParams}`,
        { headers }
      );

      if (!response.ok) throw new Error("Failed to fetch services");

      const data: ServicesResponse = await response.json();
      setServicesData(data);
    } catch (error) {
      console.error("Error fetching services data:", error);
    }
  };

  // Fetch other data (service types, equipment, providers)
  const fetchOtherData = async () => {
    try {
      const token = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const [serviceTypesRes, equipmentRes, providersRes] = await Promise.all([
        fetch(`${API_BASE}/service-types`, { headers }),
        fetch(`${API_BASE}/equipment`, { headers }),
        fetch(`${API_BASE}/users/doctors`, { headers }),
      ]);

      if (!serviceTypesRes.ok || !equipmentRes.ok || !providersRes.ok) {
        throw new Error("Failed to fetch data");
      }

      const [serviceTypesData, equipmentData, providersData] =
        await Promise.all([
          serviceTypesRes.json(),
          equipmentRes.json(),
          providersRes.json(),
        ]);

      setServiceTypes(serviceTypesData);
      setEquipment(equipmentData);
      setProviders(providersData);
    } catch (error) {
      console.error("Error fetching other data:", error);
    }
  };

  // Main fetch function
  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([fetchServicesData(), fetchOtherData()]);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    return () => {
      if (searchDebounceTimeout) {
        clearTimeout(searchDebounceTimeout);
      }
    };
  }, []);

  // Group services by equipment (same logic as before)
  const groupServicesByCategory = (
    services: MedicalService[]
  ): GroupedService[] => {
    const grouped: Record<string, GroupedService> = {};

    services.forEach((service) => {
      const groupKey = service.equipment?.id
        ? `${service.equipment.id}-${service.service_type.id}`
        : `no-equipment-${service.service_type.id}`;

      if (grouped[groupKey]) {
        grouped[groupKey].services.push(service);
      } else {
        grouped[groupKey] = {
          equipment: service.equipment || null,
          serviceType: service.service_type,
          services: [service],
          baseInfo: {
            name: service.equipment?.name || service.service_type.name,
            base_fee: service.base_fee,
            clinic_percentage: service.clinic_percentage,
            provider_percentage: service.provider_percentage,
            owner_percentage: service.owner_percentage,
            referrer_percentage: service.referrer_percentage,
            charity_percentage: service.charity_percentage,
          },
        };
      }
    });

    return Object.values(grouped);
  };

  // Helper functions
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case "consultation":
        return getIcon("stethoscope");
      case "machine":
        return getIcon("lab");
      case "treatment":
        return getIcon("pill");
      default:
        return getIcon("stethoscope");
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "consultation":
        return "#3b82f6";
      case "machine":
        return "#8b5cf6";
      case "treatment":
        return "#10b981";
      default:
        return colorTheme.primary;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      available: { bg: "#dcfce7", color: "#166534", text: "Available" },
      maintenance: { bg: "#fef3c7", color: "#92400e", text: "Maintenance" },
      broken: { bg: "#fecaca", color: "#991b1b", text: "Broken" },
      retired: { bg: "#f3f4f6", color: "#374151", text: "Retired" },
    };
    const style = styles[status as keyof typeof styles] || styles.available;

    return (
      <span
        style={{
          padding: "4px 8px",
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: "500",
          backgroundColor: style.bg,
          color: style.color,
        }}
      >
        {style.text}
      </span>
    );
  };

  // Pagination component
  const PaginationControls: React.FC<{
    category: keyof typeof categoryPages;
    currentPage: number;
    lastPage: number;
    total: number;
    onPageChange: (page: number) => void;
  }> = ({ category, currentPage, lastPage, total, onPageChange }) => {
    if (lastPage <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(lastPage, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Add first page and ellipsis if needed
    if (startPage > 1) {
      pages.push(1);
      if (startPage > 2) pages.push(-1); // -1 represents ellipsis
    }

    // Add visible page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis and last page if needed
    if (endPage < lastPage) {
      if (endPage < lastPage - 1) pages.push(-1); // -1 represents ellipsis
      pages.push(lastPage);
    }

    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          marginTop: "24px",
          flexWrap: "wrap",
        }}
        className="pagination-controls"
      >
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            padding: "8px 12px",
            background:
              currentPage === 1 ? colorTheme.gray200 : colorTheme.primary,
            color: currentPage === 1 ? colorTheme.gray500 : "white",
            border: "none",
            borderRadius: "6px",
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: "500",
            minWidth: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {getIcon("back")}
        </button>

        {/* Page numbers */}
        {pages.map((page, index) => {
          if (page === -1) {
            return (
              <span
                key={`ellipsis-${index}`}
                style={{
                  padding: "8px 4px",
                  color: colorTheme.gray500,
                  fontSize: "14px",
                }}
              >
                ...
              </span>
            );
          }

          return (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              style={{
                padding: "8px 12px",
                background:
                  currentPage === page ? colorTheme.primary : "transparent",
                color: currentPage === page ? "white" : colorTheme.gray700,
                border:
                  currentPage === page
                    ? "none"
                    : `1px solid ${colorTheme.gray300}`,
                borderRadius: "6px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: currentPage === page ? "600" : "500",
                minWidth: "44px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                if (currentPage !== page) {
                  e.currentTarget.style.background = colorTheme.gray100;
                }
              }}
              onMouseLeave={(e) => {
                if (currentPage !== page) {
                  e.currentTarget.style.background = "transparent";
                }
              }}
            >
              {page}
            </button>
          );
        })}

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === lastPage}
          style={{
            padding: "8px 12px",
            background:
              currentPage === lastPage
                ? colorTheme.gray200
                : colorTheme.primary,
            color: currentPage === lastPage ? colorTheme.gray500 : "white",
            border: "none",
            borderRadius: "6px",
            cursor: currentPage === lastPage ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: "500",
            minWidth: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {getIcon("forward")}
        </button>

        {/* Mobile-friendly info */}
        <div
          style={{
            fontSize: "12px",
            color: colorTheme.gray600,
            marginLeft: "16px",
            whiteSpace: "nowrap",
          }}
          className="pagination-info"
        >
          Page {currentPage} of {lastPage} ({total} total)
        </div>
      </div>
    );
  };

  // Handle editing a specific service from a group
  const handleEditService = (service: MedicalService) => {
    setEditingService(service);
    setShowServiceForm(true);
  };

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: colorTheme.gray50,
        }}
      >
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            textAlign: "center",
            boxShadow: "0 10px 30px rgba(0,0,0,0.1)",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: `4px solid ${colorTheme.gray200}`,
              borderTop: `4px solid ${colorTheme.primary}`,
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 16px auto",
            }}
          />
          <p>Loading Medical Services...</p>
        </div>
      </div>
    );
  }

  const categories = ["consultation", "machine", "treatment"] as const;

  return (
    <div
      style={{
        minHeight: "100vh",
        background: colorTheme.gray50,
        padding: "32px 24px",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Header with Search */}
        <div
          style={{
            background: colorTheme.cardBg,
            borderRadius: "16px",
            padding: "32px",
            marginBottom: "32px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              marginBottom: "24px",
              flexWrap: "wrap",
              gap: "16px",
            }}
          >
            <div style={{ flex: 1, minWidth: "280px" }}>
              <h1
                style={{
                  fontSize: "28px",
                  fontWeight: "700",
                  color: colorTheme.gray900,
                  margin: "0 0 8px 0",
                }}
              >
                Medical Services Management
              </h1>
              <p
                style={{
                  color: colorTheme.gray500,
                  fontSize: "16px",
                  margin: "0 0 16px 0",
                }}
              >
                Manage services, equipment, and revenue sharing
              </p>

              {/* Search Bar */}
              <div
                style={{
                  position: "relative",
                  maxWidth: "400px",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    left: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: colorTheme.gray400,
                    fontSize: "18px",
                    pointerEvents: "none",
                  }}
                >
                  {getIcon("search")}
                </div>
                <input
                  type="text"
                  placeholder="Search services, providers, or equipment..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  style={{
                    width: "100%",
                    padding: "12px 16px 12px 44px",
                    border: `2px solid ${colorTheme.gray200}`,
                    borderRadius: "12px",
                    fontSize: "14px",
                    outline: "none",
                    transition: "all 0.2s ease",
                    background: colorTheme.cardBg,
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = colorTheme.primary;
                    e.currentTarget.style.boxShadow = `0 0 0 3px ${colorTheme.primary}20`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = colorTheme.gray200;
                    e.currentTarget.style.boxShadow = "none";
                  }}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: "flex",
                gap: "12px",
                flexWrap: "wrap",
              }}
            >
              {activeTab === "services" && (
                <button
                  onClick={() => setShowServiceForm(true)}
                  style={{
                    padding: "12px 24px",
                    background: colorTheme.successGradient,
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                    minHeight: "48px",
                  }}
                >
                  <span>{getIcon("add")}</span>
                  Add Service
                </button>
              )}

              {activeTab === "equipment" && (
                <button
                  onClick={() => setShowEquipmentForm(true)}
                  style={{
                    padding: "12px 24px",
                    background: colorTheme.bgGradient,
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                    minHeight: "48px",
                  }}
                >
                  <span>{getIcon("add")}</span>
                  Add Equipment
                </button>
              )}

              {activeTab === "service-types" && (
                <button
                  onClick={() => setShowServiceTypeForm(true)}
                  style={{
                    padding: "12px 24px",
                    background: colorTheme.successGradient,
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    transition: "all 0.2s ease",
                    minHeight: "48px",
                  }}
                >
                  <span>{getIcon("add")}</span>
                  Add Service Type
                </button>
              )}
            </div>
          </div>

          {/* Search Results Summary */}
          {searchQuery && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "16px",
                padding: "12px 16px",
                background: colorTheme.gray50,
                borderRadius: "8px",
                fontSize: "14px",
                color: colorTheme.gray600,
                flexWrap: "wrap",
              }}
            >
              <span>{getIcon("search")}</span>
              <span>
                Search results for "{searchQuery}":{" "}
                {servicesData.totals.grand_total} services found
              </span>
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    fetchServicesData("", categoryPages);
                  }}
                  style={{
                    padding: "4px 8px",
                    background: "transparent",
                    border: `1px solid ${colorTheme.gray300}`,
                    borderRadius: "4px",
                    fontSize: "12px",
                    cursor: "pointer",
                    color: colorTheme.gray600,
                    marginLeft: "auto",
                  }}
                >
                  Clear
                </button>
              )}
            </div>
          )}

          {/* Tab Navigation */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {[
              {
                id: "services",
                label: "Services",
                icon: "stethoscope",
                count: servicesData.totals.grand_total,
              },
              {
                id: "equipment",
                label: "Equipment",
                icon: "lab",
                count: equipment.length,
              },
              {
                id: "service-types",
                label: "Service Types",
                icon: "clipboard",
                count: serviceTypes.length,
              },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                style={{
                  padding: "12px 24px",
                  background:
                    activeTab === tab.id ? colorTheme.primary : "transparent",
                  color: activeTab === tab.id ? "white" : colorTheme.gray600,
                  border:
                    activeTab === tab.id
                      ? "none"
                      : `1px solid ${colorTheme.gray200}`,
                  borderRadius: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                  minHeight: "44px",
                }}
              >
                <span>{getIcon(tab.icon as any)}</span>
                <span className="tab-label">{tab.label}</span>
                <span
                  style={{
                    background:
                      activeTab === tab.id
                        ? "rgba(255,255,255,0.2)"
                        : colorTheme.gray200,
                    color: activeTab === tab.id ? "white" : colorTheme.gray600,
                    padding: "2px 6px",
                    borderRadius: "10px",
                    fontSize: "12px",
                    fontWeight: "600",
                  }}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Services Tab Content */}
        {activeTab === "services" && (
          <div>
            {servicesData.totals.grand_total === 0 ? (
              <div
                style={{
                  background: colorTheme.cardBg,
                  borderRadius: "16px",
                  padding: "64px 32px",
                  textAlign: "center",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                  {getIcon("stethoscope")}
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "8px",
                  }}
                >
                  {searchQuery ? "No Services Found" : "No Services Yet"}
                </h3>
                <p
                  style={{
                    color: colorTheme.gray500,
                    marginBottom: "24px",
                  }}
                >
                  {searchQuery
                    ? `No services found matching "${searchQuery}"`
                    : "Start by adding your first medical service"}
                </p>
                {!searchQuery && (
                  <button
                    onClick={() => setShowServiceForm(true)}
                    style={{
                      padding: "12px 24px",
                      background: colorTheme.successGradient,
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      fontSize: "14px",
                      fontWeight: "600",
                      cursor: "pointer",
                    }}
                  >
                    Add First Service
                  </button>
                )}
              </div>
            ) : (
              categories.map((category) => {
                const categoryData = servicesData.categories[category];
                const categoryGroups = groupServicesByCategory(
                  categoryData.data
                );

                if (categoryGroups.length === 0) return null;

                return (
                  <div key={category} style={{ marginBottom: "32px" }}>
                    {/* Category Header */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "16px",
                        padding: "16px 24px",
                        background: colorTheme.cardBg,
                        borderRadius: "12px",
                        borderLeft: `4px solid ${getCategoryColor(category)}`,
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                        flexWrap: "wrap",
                        gap: "12px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span style={{ fontSize: "24px", marginRight: "12px" }}>
                          {getCategoryIcon(category)}
                        </span>
                        <div>
                          <h3
                            style={{
                              fontSize: "18px",
                              fontWeight: "600",
                              color: colorTheme.gray900,
                              margin: "0 0 4px 0",
                              textTransform: "capitalize",
                            }}
                          >
                            {category} Services
                          </h3>
                          <p
                            style={{
                              color: colorTheme.gray500,
                              fontSize: "14px",
                              margin: 0,
                            }}
                          >
                            {categoryData.total} service
                            {categoryData.total !== 1 ? "s" : ""} in{" "}
                            {categoryGroups.length} group
                            {categoryGroups.length !== 1 ? "s" : ""}
                            {categoryData.from && categoryData.to && (
                              <span>
                                {" "}
                                • Showing {categoryData.from}-{categoryData.to}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Services Grid */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "repeat(auto-fill, minmax(450px, 1fr))",
                        gap: "20px",
                        marginBottom: "16px",
                      }}
                      className="services-grid"
                    >
                      {categoryGroups.map((group, groupIndex) => (
                        <div
                          key={`${category}-${groupIndex}`}
                          style={{
                            background: colorTheme.cardBg,
                            borderRadius: "16px",
                            padding: "24px",
                            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                            border: "1px solid #f1f5f9",
                            transition: "all 0.2s ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-2px)";
                            e.currentTarget.style.boxShadow =
                              "0 8px 25px rgba(0, 0, 0, 0.1)";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.boxShadow =
                              "0 4px 6px rgba(0, 0, 0, 0.07)";
                          }}
                        >
                          {/* Service Header */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              marginBottom: "16px",
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <h4
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "600",
                                  color: colorTheme.gray900,
                                  margin: "0 0 4px 0",
                                }}
                              >
                                {group.baseInfo.name}
                              </h4>
                              <p
                                style={{
                                  color: colorTheme.gray500,
                                  fontSize: "14px",
                                  margin: "0 0 8px 0",
                                }}
                              >
                                {group.serviceType.name} •{" "}
                                {group.services.length} provider
                                {group.services.length > 1 ? "s" : ""}
                              </p>
                            </div>

                            {/* Edit controls */}
                            {group.services.length > 1 ? (
                              <select
                                onChange={(e) => {
                                  const serviceId = parseInt(e.target.value);
                                  const service = group.services.find(
                                    (s) => s.id === serviceId
                                  );
                                  if (service) {
                                    handleEditService(service);
                                  }
                                  e.target.value = "";
                                }}
                                style={{
                                  padding: "6px 12px",
                                  background: "transparent",
                                  border: `1px solid ${colorTheme.gray200}`,
                                  borderRadius: "6px",
                                  color: colorTheme.gray600,
                                  fontSize: "12px",
                                  cursor: "pointer",
                                }}
                              >
                                <option value="">Edit...</option>
                                {group.services.map((service) => (
                                  <option key={service.id} value={service.id}>
                                    {service.provider.first_name}{" "}
                                    {service.provider.last_name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <button
                                onClick={() =>
                                  handleEditService(group.services[0])
                                }
                                style={{
                                  padding: "6px 12px",
                                  background: "transparent",
                                  border: `1px solid ${colorTheme.gray200}`,
                                  borderRadius: "6px",
                                  color: colorTheme.gray600,
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                {getIcon("edit")} Edit
                              </button>
                            )}
                          </div>

                          {/* Providers and Equipment Info */}
                          <div
                            style={{
                              background: colorTheme.gray50,
                              borderRadius: "8px",
                              padding: "12px",
                              marginBottom: "16px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                marginBottom: group.equipment ? "8px" : "0",
                              }}
                            >
                              <span style={{ marginRight: "8px" }}>
                                {getIcon("user")}
                              </span>
                              <span
                                style={{
                                  fontSize: "14px",
                                  color: colorTheme.gray600,
                                  fontWeight: "500",
                                }}
                              >
                                {group.services
                                  .map(
                                    (service) =>
                                      `${service.provider.first_name} ${service.provider.last_name}`
                                  )
                                  .join(", ")}
                              </span>
                            </div>

                            {group.equipment && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                  }}
                                >
                                  <span style={{ marginRight: "8px" }}>
                                    {getIcon("lab")}
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "14px",
                                      color: colorTheme.gray600,
                                    }}
                                  >
                                    {group.equipment.name}
                                  </span>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  {group.services.length > 1 && (
                                    <span
                                      style={{
                                        padding: "2px 6px",
                                        background: "#fef3c7",
                                        color: "#92400e",
                                        borderRadius: "4px",
                                        fontSize: "11px",
                                        fontWeight: "600",
                                      }}
                                    >
                                      SHARED ({group.services.length})
                                    </span>
                                  )}
                                  {getStatusBadge(group.equipment.status)}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Fee & Revenue Split */}
                          <div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "12px",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "700",
                                  color: colorTheme.gray900,
                                }}
                              >
                                ${group.baseInfo.base_fee}
                              </span>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: colorTheme.gray500,
                                }}
                              >
                                Base Fee
                              </span>
                            </div>

                            {/* Revenue Breakdown */}
                            <div
                              style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "8px",
                                fontSize: "12px",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span style={{ color: colorTheme.gray600 }}>
                                  Clinic:
                                </span>
                                <span style={{ fontWeight: "500" }}>
                                  {group.baseInfo.clinic_percentage}% ($
                                  {(
                                    (group.baseInfo.base_fee *
                                      group.baseInfo.clinic_percentage) /
                                    100
                                  ).toFixed(2)}
                                  )
                                </span>
                              </div>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span style={{ color: colorTheme.gray600 }}>
                                  Provider:
                                </span>
                                <span style={{ fontWeight: "500" }}>
                                  {group.baseInfo.provider_percentage}% ($
                                  {(
                                    (group.baseInfo.base_fee *
                                      group.baseInfo.provider_percentage) /
                                    100
                                  ).toFixed(2)}
                                  )
                                </span>
                              </div>
                              {group.baseInfo.owner_percentage > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span style={{ color: colorTheme.gray600 }}>
                                    Owner:
                                  </span>
                                  <span style={{ fontWeight: "500" }}>
                                    {group.baseInfo.owner_percentage}% ($
                                    {(
                                      (group.baseInfo.base_fee *
                                        group.baseInfo.owner_percentage) /
                                      100
                                    ).toFixed(2)}
                                    )
                                  </span>
                                </div>
                              )}
                              {group.baseInfo.referrer_percentage > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span style={{ color: colorTheme.gray600 }}>
                                    Referrer:
                                  </span>
                                  <span style={{ fontWeight: "500" }}>
                                    {group.baseInfo.referrer_percentage}% ($
                                    {(
                                      (group.baseInfo.base_fee *
                                        group.baseInfo.referrer_percentage) /
                                      100
                                    ).toFixed(2)}
                                    )
                                  </span>
                                </div>
                              )}
                              {group.baseInfo.charity_percentage > 0 && (
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span style={{ color: colorTheme.gray600 }}>
                                    Charity:
                                  </span>
                                  <span style={{ fontWeight: "500" }}>
                                    {group.baseInfo.charity_percentage}% ($
                                    {(
                                      (group.baseInfo.base_fee *
                                        group.baseInfo.charity_percentage) /
                                      100
                                    ).toFixed(2)}
                                    )
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Pagination Controls */}
                    <PaginationControls
                      category={category}
                      currentPage={categoryData.current_page}
                      lastPage={categoryData.last_page}
                      total={categoryData.total}
                      onPageChange={(page) => handlePageChange(category, page)}
                    />
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Equipment Tab - Keep existing content */}
        {activeTab === "equipment" && (
          <div>
            {equipment.length === 0 ? (
              <div
                style={{
                  background: colorTheme.cardBg,
                  borderRadius: "16px",
                  padding: "64px 32px",
                  textAlign: "center",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                  {getIcon("lab")}
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "8px",
                  }}
                >
                  No Equipment Yet
                </h3>
                <p style={{ color: colorTheme.gray500, marginBottom: "24px" }}>
                  Start by adding your first piece of equipment
                </p>
                <button
                  onClick={() => setShowEquipmentForm(true)}
                  style={{
                    padding: "12px 24px",
                    background: colorTheme.successGradient,
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Add First Equipment
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))",
                  gap: "20px",
                }}
                className="equipment-grid"
              >
                {equipment.map((item) => (
                  <div
                    key={item.id}
                    style={{
                      background: colorTheme.cardBg,
                      borderRadius: "16px",
                      padding: "24px",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                      border: "1px solid #f1f5f9",
                      transition: "all 0.2s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 8px 25px rgba(0, 0, 0, 0.1)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 6px rgba(0, 0, 0, 0.07)";
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                        marginBottom: "16px",
                      }}
                    >
                      <div>
                        <h4
                          style={{
                            fontSize: "16px",
                            fontWeight: "600",
                            color: colorTheme.gray900,
                            margin: "0 0 4px 0",
                          }}
                        >
                          {item.name}
                        </h4>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "8px",
                          }}
                        >
                          <span
                            style={{
                              padding: "2px 8px",
                              background: colorTheme.primary,
                              color: "white",
                              borderRadius: "4px",
                              fontSize: "11px",
                              textTransform: "capitalize",
                            }}
                          >
                            {item.type}
                          </span>
                          {getStatusBadge(item.status)}
                        </div>
                        {item.model && (
                          <p
                            style={{
                              color: colorTheme.gray500,
                              fontSize: "14px",
                              margin: 0,
                            }}
                          >
                            Model: {item.model}
                          </p>
                        )}
                      </div>
                    </div>

                    <div
                      style={{
                        background: colorTheme.gray50,
                        borderRadius: "8px",
                        padding: "12px",
                        fontSize: "14px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "8px",
                        }}
                      >
                        <span style={{ color: colorTheme.gray600 }}>
                          Owner:
                        </span>
                        <span style={{ fontWeight: "500" }}>
                          {item.owner.first_name} {item.owner.last_name}
                        </span>
                      </div>
                      {item.operator && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                          }}
                        >
                          <span style={{ color: colorTheme.gray600 }}>
                            Operator:
                          </span>
                          <span style={{ fontWeight: "500" }}>
                            {item.operator.first_name} {item.operator.last_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Service Types Tab - Keep existing content but make responsive */}
        {activeTab === "service-types" && (
          <div>
            {Object.entries(
              serviceTypes.reduce((acc, serviceType) => {
                const category = serviceType.category;
                if (!acc[category]) acc[category] = [];
                acc[category].push(serviceType);
                return acc;
              }, {} as Record<string, ServiceType[]>)
            ).length === 0 ? (
              <div
                style={{
                  background: colorTheme.cardBg,
                  borderRadius: "16px",
                  padding: "64px 32px",
                  textAlign: "center",
                  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                }}
              >
                <div style={{ fontSize: "48px", marginBottom: "16px" }}>
                  {getIcon("clipboard")}
                </div>
                <h3
                  style={{
                    fontSize: "20px",
                    fontWeight: "600",
                    color: colorTheme.gray900,
                    marginBottom: "8px",
                  }}
                >
                  No Service Types Yet
                </h3>
                <p style={{ color: colorTheme.gray500, marginBottom: "24px" }}>
                  Start by creating your first service type
                </p>
                <button
                  onClick={() => setShowServiceTypeForm(true)}
                  style={{
                    padding: "12px 24px",
                    background: colorTheme.successGradient,
                    color: "white",
                    border: "none",
                    borderRadius: "12px",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Add First Service Type
                </button>
              </div>
            ) : (
              Object.entries(
                serviceTypes.reduce((acc, serviceType) => {
                  const category = serviceType.category;
                  if (!acc[category]) acc[category] = [];
                  acc[category].push(serviceType);
                  return acc;
                }, {} as Record<string, ServiceType[]>)
              ).map(([category, categoryTypes]) => (
                <div key={category} style={{ marginBottom: "32px" }}>
                  {/* Category Header */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "16px",
                      padding: "16px 24px",
                      background: colorTheme.cardBg,
                      borderRadius: "12px",
                      borderLeft: `4px solid ${getCategoryColor(category)}`,
                      boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <span style={{ fontSize: "24px", marginRight: "12px" }}>
                      {getCategoryIcon(category)}
                    </span>
                    <div>
                      <h3
                        style={{
                          fontSize: "18px",
                          fontWeight: "600",
                          color: colorTheme.gray900,
                          margin: "0 0 4px 0",
                          textTransform: "capitalize",
                        }}
                      >
                        {category} Service Types
                      </h3>
                      <p
                        style={{
                          color: colorTheme.gray500,
                          fontSize: "14px",
                          margin: 0,
                        }}
                      >
                        {categoryTypes.length} service type
                        {categoryTypes.length !== 1 ? "s" : ""} configured
                      </p>
                    </div>
                  </div>

                  {/* Service Types Grid */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(320px, 1fr))",
                      gap: "20px",
                    }}
                    className="service-types-grid"
                  >
                    {categoryTypes.map((serviceType) => (
                      <div
                        key={serviceType.id}
                        style={{
                          background: colorTheme.cardBg,
                          borderRadius: "16px",
                          padding: "24px",
                          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.07)",
                          border: "1px solid #f1f5f9",
                          transition: "all 0.2s ease",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-2px)";
                          e.currentTarget.style.boxShadow =
                            "0 8px 25px rgba(0, 0, 0, 0.1)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow =
                            "0 4px 6px rgba(0, 0, 0, 0.07)";
                        }}
                      >
                        {/* Service Type Header */}
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: "16px",
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <h4
                              style={{
                                fontSize: "16px",
                                fontWeight: "600",
                                color: colorTheme.gray900,
                                margin: "0 0 4px 0",
                              }}
                            >
                              {serviceType.name}
                            </h4>
                            {serviceType.description && (
                              <p
                                style={{
                                  color: colorTheme.gray500,
                                  fontSize: "14px",
                                  margin: "0 0 8px 0",
                                }}
                              >
                                {serviceType.description}
                              </p>
                            )}
                          </div>
                          <button
                            onClick={() => {
                              setEditingServiceType(serviceType);
                              setShowServiceTypeForm(true);
                            }}
                            style={{
                              padding: "6px 12px",
                              background: "transparent",
                              border: `1px solid ${colorTheme.gray200}`,
                              borderRadius: "6px",
                              color: colorTheme.gray600,
                              fontSize: "12px",
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                              gap: "4px",
                            }}
                          >
                            {getIcon("edit")} Edit
                          </button>
                        </div>

                        {/* Service Type Details */}
                        <div
                          style={{
                            background: colorTheme.gray50,
                            borderRadius: "8px",
                            padding: "12px",
                            marginBottom: "16px",
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px",
                              fontSize: "14px",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span style={{ color: colorTheme.gray600 }}>
                                Duration:
                              </span>
                              <span style={{ fontWeight: "500" }}>
                                {serviceType.duration} min
                              </span>
                            </div>
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              <span style={{ color: colorTheme.gray600 }}>
                                Equipment:
                              </span>
                              <span style={{ fontWeight: "500" }}>
                                {serviceType.requires_equipment
                                  ? "Required"
                                  : "Optional"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Category Badge */}
                        <div
                          style={{ display: "flex", justifyContent: "center" }}
                        >
                          <span
                            style={{
                              padding: "6px 12px",
                              background: `${getCategoryColor(category)}15`,
                              color: getCategoryColor(category),
                              borderRadius: "8px",
                              fontSize: "12px",
                              fontWeight: "600",
                              textTransform: "capitalize",
                              border: `1px solid ${getCategoryColor(
                                category
                              )}30`,
                            }}
                          >
                            {getCategoryIcon(category)} {category}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showServiceForm && (
        <ServiceForm
          isOpen={showServiceForm}
          onClose={() => {
            setShowServiceForm(false);
            setEditingService(null);
          }}
          onSave={fetchData}
          editingService={editingService}
          serviceTypes={serviceTypes}
          providers={providers}
          equipment={equipment}
          existingServices={[]} // Pass flattened services if needed
        />
      )}

      {showEquipmentForm && (
        <EquipmentForm
          isOpen={showEquipmentForm}
          onClose={() => setShowEquipmentForm(false)}
          onSave={fetchData}
          providers={providers}
        />
      )}

      {showServiceTypeForm && (
        <ServiceTypeForm
          isOpen={showServiceTypeForm}
          onClose={() => {
            setShowServiceTypeForm(false);
            setEditingServiceType(null);
          }}
          onSave={fetchData}
          editingServiceType={editingServiceType}
        />
      )}

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          /* Responsive adjustments */
          @media (max-width: 768px) {
            .services-grid {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
            }
            
            .equipment-grid {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
            }
            
            .service-types-grid {
              grid-template-columns: 1fr !important;
              gap: 16px !important;
            }
            
            .pagination-controls {
              justify-content: flex-start !important;
              overflow-x: auto !important;
              padding-bottom: 8px;
            }
            
            .pagination-info {
              display: none !important;
            }
            
            .tab-label {
              display: none !important;
            }
          }

          @media (min-width: 769px) and (max-width: 1024px) {
            .services-grid {
              grid-template-columns: repeat(auto-fill, minmax(400px, 1fr)) !important;
            }
            
            .equipment-grid {
              grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;
            }
            
            .service-types-grid {
              grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important;
            }
          }

          @media (max-width: 480px) {
            .services-grid {
              gap: 12px !important;
            }
            
            .equipment-grid {
              gap: 12px !important;
            }
            
            .service-types-grid {
              gap: 12px !important;
            }
            
            .pagination-controls button {
              min-width: 36px !important;
              padding: 6px 8px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default MedicalServicesPage;
